import { supabase } from '../lib/supabase'
import { Buyer } from '../types'

/*
 * The two ABR endpoints now require a signed-in caller, so the caller's Supabase
 * access token goes on the request. Without it anyone who found the URL could
 * spend Direct Auto's ABR quota from a blank browser tab.
 */
async function abrAuthHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/**
 * Wholesale buyers.
 *
 * Buyer details used to be re-typed onto every deal: name, ABN, LMCT and email
 * as columns on `deals`. That made two things in §5 impossible rather than
 * merely missing. You could not look up a returning buyer (§5.2), and you could
 * not freeze an account and flag all of that buyer's deals (§5.4), because
 * there was no "that buyer" to freeze — only a string repeated across rows.
 *
 * The deal columns are still written, so every existing report keeps working;
 * they are now a copy of the buyer record at the time of sale rather than the
 * only place the information lives.
 */

/* The checksum moved to src/lib/abn.ts so the invoice reader can use it without pulling
   in this service (and its Supabase client) — Winnie's 13 Sep note asks for one ABN
   algorithm, not a second one. Re-exported here under the names the app already imports. */
export { ABN_RE, isValidAbn } from '../lib/abn'
import { isValidAbn } from '../lib/abn'
/* Winnie, 15 Sep: the Contacts registry is consulted before the external ABN Lookup API. */
import { contactsService } from './contactsService'

export const MOBILE_RE = /^(?:\+?61|0)4\d{8}$/

export interface BuyerInput {
  name: string
  legal_name?: string | null
  trading_name?: string | null
  abn?: string | null
  lmct?: string | null
  email?: string | null
  mobile?: string | null
  phone?: string | null
  billing_address?: string | null
  delivery_address?: string | null
}

export interface AbrResult {
  ok: boolean
  message: string
  legalName?: string | null
  tradingNames?: string[]
  entityType?: string | null
  gstRegistered?: boolean
  abnStatus?: string | null
  /**
   * Where the answer came from (Winnie, 15 Sep). 'contacts' means the Contacts registry
   * answered and the ABR was never called; 'abr' means the register answered, which is what
   * lets the screen offer to save the result as a new Contact.
   */
  source?: 'contacts' | 'abr'
  /** Set when source is 'contacts' — the record that answered. */
  contactId?: string | null
}

/**
 * Look an ABN up on the Australian Business Register.
 *
 * Used in two places the spec cares about: filling the legal name on a tax
 * invoice (§5.3), and confirming whether a seller is GST-registered, which is
 * what decides the Division 66 treatment (§4.4). Returns a plain message rather
 * than throwing when the register is not configured, because the form should
 * carry on working by hand.
 */
export async function lookupAbn(abn: string): Promise<AbrResult> {
  if (!isValidAbn(abn)) {
    return { ok: false, message: 'That ABN does not pass the checksum.' }
  }

  /**
   * The Contacts registry first (Winnie, 15 Sep). A business we have already dealt with does
   * not need a second trip to the register, and the quota is finite.
   *
   * The registry only answers when it knows the GST status — see the long note at the top of
   * contactsService. A contact somebody typed into Admin by hand has no GST status, so it
   * fills the name on screen but does not stop the ABR being asked, because GST registration
   * is what decides the margin-scheme treatment on the deal.
   *
   * A registry failure is never fatal: it falls through to the ABR exactly as before.
   */
  try {
    const known = await contactsService.lookupFromRegistry(abn)
    if (known) {
      const name = known.legal_name || known.name
      return {
        ok: true,
        message: `${name} — ${known.abn_status || 'from Contacts'}, ${
          known.gst_registered ? 'GST registered' : 'not GST registered'
        }. Known contact ${known.contact_number || ''}`.trim(),
        legalName: name,
        tradingNames: known.legal_name && known.name !== known.legal_name ? [known.name] : [],
        entityType: null,
        gstRegistered: !!known.gst_registered,
        abnStatus: known.abn_status,
        source: 'contacts',
        contactId: known.id,
      }
    }
  } catch (e) {
    console.warn('Contacts registry unavailable; going to the ABR.', e)
  }

  let res: Response
  try {
    res = await fetch(`/api/abr/lookup?abn=${encodeURIComponent(abn.replace(/\s/g, ''))}`, {
      headers: await abrAuthHeaders(),
    })
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : 'Could not reach the ABR.' }
  }

  if (!(res.headers.get('content-type') || '').includes('application/json')) {
    return { ok: false, message: 'ABN Lookup is not deployed on this environment.' }
  }

  const body = await res.json().catch(() => ({}) as any)
  if (!res.ok || !body?.success) {
    return { ok: false, message: body?.error || `ABN Lookup failed (${res.status}).` }
  }

  return {
    ok: true,
    message: `${body.legalName || 'Entity'} — ${body.abnStatus || 'unknown status'}, ${
      body.gstRegistered ? 'GST registered' : 'not GST registered'
    }.`,
    legalName: body.legalName,
    tradingNames: body.tradingNames || [],
    entityType: body.entityType,
    gstRegistered: !!body.gstRegistered,
    abnStatus: body.abnStatus,
    source: 'abr',
    contactId: null,
  }
}

/** Find buyers matching a free-text term — ABN, name, email or trading name. */
export async function searchBuyers(term: string, limit = 8): Promise<Buyer[]> {
  const q = (term || '').trim()
  if (q.length < 2) return []

  const like = `%${q}%`
  const { data, error } = await supabase
    .from('buyers')
    .select('*')
    .or(`name.ilike.${like},trading_name.ilike.${like},legal_name.ilike.${like},email.ilike.${like},abn.ilike.${like}`)
    .order('name')
    .limit(limit)

  if (error) throw error
  return (data || []) as Buyer[]
}

/** Exact match on the two things that identify a buyer beyond doubt. */
export async function findBuyer(abn?: string | null, email?: string | null): Promise<Buyer | null> {
  if (abn) {
    const { data } = await supabase.from('buyers').select('*').eq('abn', abn).maybeSingle()
    if (data) return data as Buyer
  }
  if (email) {
    const { data } = await supabase
      .from('buyers')
      .select('*')
      .ilike('email', email)
      .maybeSingle()
    if (data) return data as Buyer
  }
  return null
}

export async function getBuyer(id: string): Promise<Buyer | null> {
  const { data, error } = await supabase.from('buyers').select('*').eq('id', id).maybeSingle()
  if (error) throw error
  return (data as Buyer) || null
}

export async function listBuyers(): Promise<Buyer[]> {
  const { data, error } = await supabase.from('buyers').select('*').order('name')
  if (error) throw error
  return (data || []) as Buyer[]
}

/**
 * Create or update, matched on ABN then email so the same dealership does not
 * accumulate a row per deal.
 */
export async function upsertBuyer(input: BuyerInput, id?: string): Promise<Buyer> {
  const payload = {
    name: input.name.trim(),
    legal_name: input.legal_name?.trim() || null,
    trading_name: input.trading_name?.trim() || null,
    abn: input.abn?.replace(/\s/g, '') || null,
    lmct: input.lmct?.trim() || null,
    email: input.email?.trim() || null,
    mobile: input.mobile?.trim() || null,
    phone: input.phone?.trim() || null,
    billing_address: input.billing_address?.trim() || null,
    delivery_address: input.delivery_address?.trim() || null,
    updated_at: new Date().toISOString(),
  }

  const target = id ? { id } : await findBuyer(payload.abn, payload.email)

  if (target) {
    const { data, error } = await supabase
      .from('buyers')
      .update(payload)
      .eq('id', (target as any).id)
      .select()
      .single()
    if (error) throw error
    return data as Buyer
  }

  const { data, error } = await supabase.from('buyers').insert([payload]).select().single()
  if (error) throw error
  return data as Buyer
}

/* ------------------------------------------------------------------ *
 * §5.4 — credit control
 * ------------------------------------------------------------------ */

export type CreditStatus = Buyer['credit_status']

export const CREDIT_STATUS_LABEL: Record<CreditStatus, string> = {
  ok: 'Good standing',
  suspended: 'Credit suspended',
  frozen: 'Account frozen',
  collections: 'With collections',
}

/**
 * What the ladder means in practice, rather than as a timestamp:
 *
 *   suspended  (7–14 days overdue)  no new sales to this buyer
 *   frozen     (14–30 days)         every open deal for them is flagged too
 *   collections (30+)               referred out
 */
export async function setCreditStatus(
  buyerId: string,
  status: CreditStatus,
  reason: string
): Promise<void> {
  const { error } = await supabase
    .from('buyers')
    .update({
      credit_status: status,
      credit_status_at: new Date().toISOString(),
      credit_status_reason: reason,
      updated_at: new Date().toISOString(),
    })
    .eq('id', buyerId)
  if (error) throw error
}

/** Every deal for a buyer whose account is not in good standing. */
export async function flaggedDealsFor(buyerId: string): Promise<
  { id: string; vehicle: string; deal_status: string }[]
> {
  const { data, error } = await supabase
    .from('deals')
    .select('id, deal_status, vehicle_year, vehicle_make, vehicle_model, autograb_year, autograb_make, autograb_model')
    .eq('buyer_id', buyerId)
    .is('deleted_at', null)
    .not('deal_status', 'in', '("closed","cancelled")')
  if (error) throw error

  return (data || []).map((d: any) => ({
    id: d.id,
    deal_status: d.deal_status,
    vehicle: [d.vehicle_year ?? d.autograb_year, d.vehicle_make ?? d.autograb_make, d.vehicle_model ?? d.autograb_model]
      .filter(Boolean)
      .join(' '),
  }))
}

/* ------------------------------------------------------------------ *
 * §5.4 — AR overdue risk rating
 * ------------------------------------------------------------------ */

export interface RiskAssessment {
  rating: Buyer['risk_rating']
  score: number
  paymentsConsidered: number
  worstDaysLate: number
  weightedAverageDaysLate: number
  reason: string
}

/** The lookback the spec asks for. */
export const RISK_LOOKBACK_PAYMENTS = 12
/** A single payment beyond this makes a buyer high risk, whatever the average. */
export const RISK_HIGH_SINGLE_PAYMENT_DAYS = 30

/**
 * Rate a buyer from how they have actually paid.
 *
 * Two rules from §5.4 fight each other slightly, and the resolution matters:
 * recent payments are weighted more heavily, but one payment over 30 days makes
 * the buyer High Risk regardless. So the weighting decides low-versus-medium,
 * and the single-payment rule is an override on top — otherwise one very late
 * invoice would be averaged away by a run of prompt ones, which is exactly the
 * behaviour the rule exists to prevent.
 */
export function assessRisk(
  payments: { dueDate: string; paidAt: string }[]
): RiskAssessment {
  const recent = [...payments]
    .filter((p) => p.dueDate && p.paidAt)
    .sort((a, b) => new Date(b.paidAt).getTime() - new Date(a.paidAt).getTime())
    .slice(0, RISK_LOOKBACK_PAYMENTS)

  if (recent.length === 0) {
    return {
      rating: 'unrated',
      score: 0,
      paymentsConsidered: 0,
      worstDaysLate: 0,
      weightedAverageDaysLate: 0,
      reason: 'No settled invoices yet.',
    }
  }

  const daysLate = recent.map((p) =>
    Math.max(
      0,
      Math.round(
        (new Date(p.paidAt).getTime() - new Date(p.dueDate).getTime()) / 86_400_000
      )
    )
  )

  // Linear recency weights: the most recent payment counts n times, the oldest
  // once. Simple, explainable to a person looking at the number, and monotonic.
  const n = daysLate.length
  let weighted = 0
  let weightSum = 0
  daysLate.forEach((d, i) => {
    const w = n - i
    weighted += d * w
    weightSum += w
  })
  const weightedAvg = Math.round((weighted / weightSum) * 10) / 10
  const worst = Math.max(...daysLate)

  let rating: Buyer['risk_rating']
  let reason: string

  if (worst > RISK_HIGH_SINGLE_PAYMENT_DAYS) {
    rating = 'high'
    reason = `One invoice was settled ${worst} days past due — a single payment beyond ${RISK_HIGH_SINGLE_PAYMENT_DAYS} days rates the account High.`
  } else if (weightedAvg > 7) {
    rating = 'medium'
    reason = `Recent invoices are settling ${weightedAvg} days late on a recency-weighted average.`
  } else {
    rating = 'low'
    reason = `Recent invoices settle ${weightedAvg} days late on a recency-weighted average.`
  }

  return {
    rating,
    score: weightedAvg,
    paymentsConsidered: n,
    worstDaysLate: worst,
    weightedAverageDaysLate: weightedAvg,
    reason,
  }
}

/**
 * Recalculate and store a buyer's rating.
 *
 * Called after every payment event, which is what §5.4 asks for — a rating that
 * is only refreshed when someone opens a report is a rating nobody trusts.
 */
export async function recalculateRisk(buyerId: string): Promise<RiskAssessment> {
  const { data, error } = await supabase
    .from('invoices')
    .select('due_date, payment_received_at')
    .eq('buyer_id', buyerId)
    .not('payment_received_at', 'is', null)
    .order('payment_received_at', { ascending: false })
    .limit(RISK_LOOKBACK_PAYMENTS)

  if (error) throw error

  const assessment = assessRisk(
    (data || []).map((r: any) => ({ dueDate: r.due_date, paidAt: r.payment_received_at }))
  )

  await supabase
    .from('buyers')
    .update({
      risk_rating: assessment.rating,
      risk_score: assessment.score,
      risk_calculated_at: new Date().toISOString(),
      risk_basis: {
        paymentsConsidered: assessment.paymentsConsidered,
        worstDaysLate: assessment.worstDaysLate,
        weightedAverageDaysLate: assessment.weightedAverageDaysLate,
        reason: assessment.reason,
      },
      updated_at: new Date().toISOString(),
    })
    .eq('id', buyerId)

  return assessment
}

/*
 * Search the register by business or trading name.
 *
 * The other direction to lookupAbn. The sales team is usually handed a trading name
 * rather than an ABN, and a trading-name hit comes back as the trading name - not the
 * legal entity name - so the caller follows up with lookupAbn on whichever ABN was
 * chosen. Like lookupAbn, this returns a plain message rather than throwing when the
 * register is not configured, so the form carries on working by hand.
 */
export interface AbrNameMatch {
  abn: string
  abnStatus: string | null
  name: string
  nameType: string | null
  state: string | null
  postcode: string | null
}

export interface AbrSearchResult {
  ok: boolean
  message: string
  results: AbrNameMatch[]
}

export async function searchAbnByName(name: string): Promise<AbrSearchResult> {
  const term = (name || '').trim()
  if (term.length < 3) {
    return { ok: false, message: 'Search for at least three characters.', results: [] }
  }

  let res: Response
  try {
    res = await fetch(`/api/abr/search?name=${encodeURIComponent(term)}`, { headers: await abrAuthHeaders() })
  } catch (e) {
    return {
      ok: false,
      message: e instanceof Error ? e.message : 'Could not reach the ABR.',
      results: [],
    }
  }

  if (!(res.headers.get('content-type') || '').includes('application/json')) {
    return { ok: false, message: 'ABN Lookup is not deployed on this environment.', results: [] }
  }

  const body = await res.json().catch(() => ({}) as any)
  if (!res.ok || !body?.success) {
    return { ok: false, message: body?.error || `ABN search failed (${res.status})`, results: [] }
  }

  const results: AbrNameMatch[] = Array.isArray(body.results) ? body.results : []

  /*
   * An empty list is a real answer, not a failure - it means the register has nothing
   * under that name. Saying so plainly stops it reading as a broken search.
   */
  if (results.length === 0) {
    return { ok: true, message: `Nothing on the register matches "${term}".`, results: [] }
  }

  return {
    ok: true,
    message: `${results.length} match${results.length === 1 ? '' : 'es'} on the register. Pick one to fill in the ABN and legal name.`,
    results,
  }
}
